import { useState } from 'react';
import useDispatch from 'client/hooks/useDispatch';

import {
  INVENTORY_ITEM_STATUSES_OPTIONS,
  INVENTORY_ITEM_TYPE_TO_LABEL_MAP,
} from 'common/inventoryConstants';

import postWithState from 'client/actions/postWithState';
import {
  SR_API_ACTION_INVENTORY_ITEM_GET_BY_SERIAL_NUMBER,
  SR_API_ACTION_INVENTORY_ITEM_UPDATE,
} from 'api/actionTypes';

const useSrSunSetupAdminUpdateItemController = () => {
  const [itemBeingUpdated, setItemBeingUpdated] = useState(null);
  const [itemUpdateError, setItemUpdateError] = useState(null);
  const [buttonLabel, setButtonLabel] = useState('Find');
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  const getFindItemFormFields = () => {
    return [
      {
        id: 'serialNumber',
        title: 'Item Serial Number',
        type: 'short_text',
        validations: ['required'],
      },
    ];
  };

  const getUpdateItemFormFields = () => {
    return [
      {
        id: 'status',
        title: 'Item Status',
        type: 'select',
        properties: {
          choices: INVENTORY_ITEM_STATUSES_OPTIONS,
        },
        validations: ['required'],
      },
    ];
  };

  const findInventoryItem = (values) => {
    const serialNumber = values?.serialNumber;
    setIsLoading(true);
    if (!serialNumber) {
      setItemBeingUpdated(null);
      setItemUpdateError('Please enter a serial number');
      return;
    }

    return dispatch(postWithState, {
      action: SR_API_ACTION_INVENTORY_ITEM_GET_BY_SERIAL_NUMBER,
      payload: {
        serialNumber,
      },
      onSuccess: (response) => {
        if (response) {
          setItemBeingUpdated(response);
          setItemUpdateError(null);
          setButtonLabel('Update');
        } else {
          setItemBeingUpdated(null);
          setItemUpdateError('Item not found');
          setButtonLabel('Find');
        }
      },
      onError: () => {
        setItemBeingUpdated(null);
        setItemUpdateError('Item not found');
        setButtonLabel('Find');
      },
    }).then(() => {
      setIsLoading(false);
    });
  };

  const updateInventoryItem = (values) => {
    if (!itemBeingUpdated || !itemBeingUpdated?.id) {
      return;
    }

    return dispatch(postWithState, {
      action: SR_API_ACTION_INVENTORY_ITEM_UPDATE,
      payload: {
        id: itemBeingUpdated?.id,
        updates: values,
      },
      onSuccess: (response) => {
        if (response) {
          setItemBeingUpdated(null);
          setItemUpdateError(null);
          setButtonLabel('Updated!');

          setTimeout(() => {
            setButtonLabel('Find');
          }, 3000);
        } else {
          setItemBeingUpdated(null);
          setItemUpdateError('There was an error updating the item, please try again.');
          setButtonLabel('Find');
        }
      },
      onError: () => {
        setItemBeingUpdated(null);
        setItemUpdateError('There was an error updating the item, please try again.');
        setButtonLabel('Find');
      },
    }).then(() => {
      setIsLoading(false);
    });
  };

  const getInventoryItemName = () => {
    if (!itemBeingUpdated) {
      return null;
    }
    return INVENTORY_ITEM_TYPE_TO_LABEL_MAP[itemBeingUpdated?.inventoryItemTypeId];
  };

  const cancelSearch = () => {
    setItemBeingUpdated(null);
    setItemUpdateError(null);
    setButtonLabel('Find');
  };

  return {
    state: {
      itemBeingUpdated,
      itemUpdateError,
      isLoading,
      buttonLabel,
    },
    form: {
      isLoading: false,
      displayCancelButton: false,
      itemType: getInventoryItemName(),
      itemSerialNumber: itemBeingUpdated?.serialNumber,
      itemOwnerName: itemBeingUpdated?.ownerName,
    },
    actions: {
      getFindItemFormFields,
      getUpdateItemFormFields,
      cancelSearch,
      findInventoryItem,
      updateInventoryItem,
    },
  };
};

export default useSrSunSetupAdminUpdateItemController;
